import React, { useState, useEffect, useRef } from "react";
import { RingLoader } from "react-spinners";
import styled from "styled-components";
import ReactAudioPlayer from "react-audio-player";
import Switch from "react-switch";
import { toast, ToastContainer, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledButton = styled.button`
    background-color: #48bb78;
    &:hover {
        background-color: #38a169;
    }
    color: white;
    font-size: 1.5em;
    padding: 10px 20px;
    border-radius: 5px;
    font-weight: bold;
`;

const StyledButtonDelete = styled.button`
    background-color: #f44336;
    &:hover {
        background-color: #e53935;
    }
    color: white;
    font-size: 1.5em;
    padding: 10px 20px;
    border-radius: 5px;
    font-weight: bold;
`;

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100%;
`;

const StyledMessage = styled.div`
    text-align: center;
    color: #48bb78;
    font-size: 28px;
    margin-bottom: 10px;
`;

const FileBlock = styled.div`
    border: 2px solid ${(props) => props.color};
    border-radius: 10px;
    margin-bottom: 20px;
    padding: 10px;
`;

const generateRandomColor = () =>
    "#" + Math.floor(Math.random() * 16777215).toString(16);

const formatDuration = (ms) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${hours}ч ${minutes}м ${seconds}с`;
};

const Overlay = (props) => {
    const {
        titleDownload,
        titleDelete,
        titleReady,
        titleCancel,
        titleOverlay,
        titleStart,
        titleEnd,
        titleIndent,
        titleShowHideTimeSettings,
        titleChangeStripeColors,
        titleDuration,
        titleLongestFile,
        titleOverlayResult,
        tip4,
    } = props.attributes;

    const [fileData, setFileData] = useState([]);
    const [colors, setColors] = useState([]);
    const [showInputs, setShowInputs] = useState(false);
    const completedRequests = useRef(0);
    const [progress, setProgress] = useState(0);
    const [positions, setPositions] = useState([]);
    const [switchValue2, setSwitchValue2] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessingFinished, setIsProcessingFinished] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showOverlay, setShowOverlay] = useState(true);
    const [indents, setIndents] = useState([]);
    const [errorCount, setErrorCount] = useState(0);

    const fetchFileData = () => {
        completedRequests.current = 0;
        setErrorCount(0); // Сбросить счетчик ошибок при каждом новом запуске
        setIsLoading(true);

        let validFilesList = [...props.filesList];
        const totalFiles = props.filesList.length;
        const fetchData = [];

        validFilesList.forEach((file) => {
            const timestamp = Date.now();
            fetch(`/api/avmergeinfo/${file.fileId}?timestamp=${timestamp}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Cache-Control": "no-cache",
                    "X-CSRF-TOKEN": document
                        .querySelector('meta[name="csrf-token"]')
                        .getAttribute("content"),
                },
            })
                .then((response) => {
                    console.log("Response status:", response.status);
                    if (!response.ok) {
                        return response.json().then((error) => {
                            if (response.status === 470) {
                                validFilesList = validFilesList.filter(
                                    (f) => f.fileId !== file.fileId
                                );
                                props.setFilesList(validFilesList);
                            }
                            throw new Error(
                                error.message || "Error fetching data"
                            );
                        });
                    }
                    return response.json();
                })
                .then((data) => {
                    completedRequests.current++;
                    fetchData.push({ ...data, fileId: file.fileId });

                    if (completedRequests.current === totalFiles) {
                        setFileData(fetchData);
                        setPositions(
                            fetchData.map((file) => ({
                                start: 0,
                                end: file.duration,
                            }))
                        );
                        setIndents(fetchData.map(() => 0));
                        setColors(fetchData.map(generateRandomColor));
                        setIsLoading(false); // Завершаем состояние загрузки
                    }

                    const currentProgress =
                        (completedRequests.current / totalFiles) * 100;
                    setProgress(currentProgress);
                })
                .catch((error) => {
                    setErrorCount((prevCount) => prevCount + 1);
                    toast.error(error.message || "Error fetching data", {
                        position: "top-center",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                        transition: Bounce,
                    });
                    console.error("An error occurred:", error);
                    setShowOverlay(true);
                });
        });
        setShowOverlay(true);
    };

    useEffect(() => {
        fetchFileData();
    }, [props.filesList /* , errorCount */]);

    if (isLoading || fileData.length === 0) {
        return (
            <div style={{ padding: "20px" }}>
                <div>({Math.round(progress)}%)</div>
                <div
                    style={{
                        width: "100%",
                        height: "20px",
                        backgroundColor: "#e0e0e0",
                        margin: "20px 0",
                    }}
                >
                    <div
                        style={{
                            width: `${progress}%`,
                            height: "100%",
                            backgroundColor: "#4CAF50",
                        }}
                    ></div>
                </div>
                {isLoading && (
                    <LoaderContainer>
                        <RingLoader
                            color={"#123abc"}
                            loading={isLoading}
                            size={150}
                        />
                    </LoaderContainer>
                )}
            </div>
        );
    }

    const durationsInMs = fileData.map((file) => file.duration);
    const maxDuration = Math.max(...durationsInMs);
    const maxIndex = durationsInMs.indexOf(maxDuration);

    const handleStartChange = (index, event) => {
        const newStart = parseFloat(event.target.value);
        setPositions((prevPositions) => {
            const newPositions = [...prevPositions];
            newPositions[index].start = newStart;
            newPositions[index].end = Math.min(
                newStart + durationsInMs[index],
                maxDuration
            );
            return newPositions;
        });

        setIndents((prevIndents) => {
            const newIndents = [...prevIndents];
            newIndents[index] = newStart;
            return newIndents;
        });
    };

    const handleEndChange = (index, event) => {
        const newEnd = parseFloat(event.target.value);
        setPositions((prevPositions) => {
            const newPositions = [...prevPositions];
            newPositions[index].end = Math.min(newEnd, maxDuration);
            newPositions[index].start =
                newPositions[index].end - durationsInMs[index];
            return newPositions;
        });

        setIndents((prevIndents) => {
            const newIndents = [...prevIndents];
            const newPositions = [...positions];
            newIndents[index] = newPositions[index].start;
            return newIndents;
        });
    };

    const handleTimeChange = (index, type, unit, value) => {
        setPositions((prevPositions) => {
            const newPositions = [...prevPositions];
            const durationInMs = durationsInMs[index];
            const currentStart = newPositions[index].start;
            const currentEnd = newPositions[index].end;
            const currentTotal = type === "start" ? currentStart : currentEnd;

            const hours = Math.floor(currentTotal / 3600000);
            const minutes = Math.floor((currentTotal % 3600000) / 60000);
            const seconds = Math.floor((currentTotal % 60000) / 1000);
            const milliseconds = currentTotal % 1000;

            let newValue = 0;

            if (unit === "hours") {
                newValue =
                    value * 3600000 +
                    minutes * 60000 +
                    seconds * 1000 +
                    milliseconds;
            } else if (unit === "minutes") {
                newValue =
                    hours * 3600000 +
                    value * 60000 +
                    seconds * 1000 +
                    milliseconds;
            } else if (unit === "seconds") {
                newValue =
                    hours * 3600000 +
                    minutes * 60000 +
                    value * 1000 +
                    milliseconds;
            } else {
                newValue =
                    hours * 3600000 + minutes * 60000 + seconds * 1000 + value;
            }

            if (type === "start") {
                newPositions[index].start = Math.min(
                    newValue,
                    maxDuration - durationInMs
                );
                newPositions[index].end = Math.min(
                    newPositions[index].start + durationInMs,
                    maxDuration
                );
            } else {
                newPositions[index].end = Math.min(newValue, maxDuration);
                newPositions[index].start =
                    newPositions[index].end - durationInMs;
            }

            return newPositions;
        });
    };

    const handleOverlay = async () => {
        document.body.scrollIntoView({ behavior: "smooth", block: "start" });

        setIsLoading(true);
        setShowOverlay(false);

        const sliderValuesWithFileId = fileData.map((file, index) => ({
            fileId: file.fileId,
            sliderValue: [positions[index].start, positions[index].end],
        }));

        const requestBody = {
            cards: sliderValuesWithFileId,
            switchValue2: switchValue2,
        };

        const timestamp = Date.now();

        fetch(`/api/overlayVideo?timestamp=${timestamp}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Cache-Control": "no-cache",
            },
            body: JSON.stringify(requestBody),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json().then((responseData) => {
                        if (response.status === 471) {
                            throw new Error(
                                responseData.message ||
                                    "Video or audio file not found"
                            );
                        }
                        throw new Error(
                            responseData.message || "An error occurred"
                        );
                    });
                }
                return response.json();
            })
            .then((responseData) => {
                setIsProcessingFinished(true);
                setDownloadUrl(responseData.url);
                setIsLoading(false);
                setIsDownloading(false);
                props.setShowUploader(false);
                document.body.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            })
            .catch((error) => {
                toast.error(error.message || "Error fetching data", {
                    position: "top-center",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                    transition: Bounce,
                });
                console.error("Error overlaying audio:", error);
                setIsLoading(false);
                setShowOverlay(true);
            });
    };

    const renderTimeInputs = (index, type, value) => {
        const hours = Math.floor(value / 3600000);
        const minutes = Math.floor((value % 3600000) / 60000);
        const seconds = Math.floor((value % 60000) / 1000);
        const milliseconds = value % 1000;

        const maxHours = Math.floor(
            (maxDuration - (type === "start" ? durationsInMs[index] : 0)) /
                3600000
        );

        return (
            <div style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
                <input
                    type="number"
                    min="0"
                    max={maxHours}
                    value={hours}
                    onChange={(e) =>
                        handleTimeChange(
                            index,
                            type,
                            "hours",
                            parseInt(e.target.value)
                        )
                    }
                    style={{ width: "100px" }}
                />
                <span>:</span>
                <input
                    type="number"
                    min="0"
                    max="59"
                    value={minutes}
                    onChange={(e) =>
                        handleTimeChange(
                            index,
                            type,
                            "minutes",
                            parseInt(e.target.value)
                        )
                    }
                    style={{ width: "100px" }}
                />
                <span>:</span>
                <input
                    type="number"
                    min="0"
                    max="59"
                    value={seconds}
                    onChange={(e) =>
                        handleTimeChange(
                            index,
                            type,
                            "seconds",
                            parseInt(e.target.value)
                        )
                    }
                    style={{ width: "100px" }}
                />
                <span>.</span>
                <input
                    type="number"
                    min="0"
                    max="999"
                    value={milliseconds}
                    onChange={(e) =>
                        handleTimeChange(
                            index,
                            type,
                            "milliseconds",
                            parseInt(e.target.value)
                        )
                    }
                    style={{ width: "70px" }}
                />
            </div>
        );
    };

    const renderSlider = (index) => (
        <FileBlock key={index} color={colors[index]}>
            <>
                <input
                    type="range"
                    min="0"
                    max={maxDuration - durationsInMs[index]}
                    value={positions[index].start}
                    onChange={(e) => handleStartChange(index, e)}
                    style={{ width: "48%", marginRight: "10px" }}
                    disabled={index === maxIndex}
                />
                <input
                    type="range"
                    min={durationsInMs[index]}
                    max={maxDuration}
                    value={positions[index].end}
                    onChange={(e) => handleEndChange(index, e)}
                    style={{ width: "48%" }}
                    disabled={index === maxIndex}
                />
                <div>
                    {titleDuration}: {formatDuration(fileData[index].duration)},{" "}
                    {titleStart}: {formatDuration(positions[index].start)},{" "}
                    {titleEnd}: {formatDuration(positions[index].end)},{" "}
                    {titleIndent}: {formatDuration(indents[index])}
                </div>
                {showInputs && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <div>
                            {titleStart}:{" "}
                            {renderTimeInputs(
                                index,
                                "start",
                                positions[index].start
                            )}
                        </div>
                        <div>
                            {titleEnd}:{" "}
                            {renderTimeInputs(
                                index,
                                "end",
                                positions[index].end
                            )}
                        </div>
                    </div>
                )}
                <ReactAudioPlayer src={fileData[index].url} controls />
            </>
        </FileBlock>
    );

    const changeColors = () => {
        setColors(fileData.map(generateRandomColor));
    };

    const handleDownload = (e) => {
        e.stopPropagation();
        setIsDownloading(true);
        props.setShowUploader(false);
        document.body.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleChangeSwitch2 = () => {
        setSwitchValue2(!switchValue2);
    };

    const resetDownload = () => {
        setIsDownloading(false);
        setIsProcessingFinished(false);
        setDownloadUrl(null);
        setShowOverlay(true);
        props.setShowUploader(true);
    };

    function getFileName(url) {
        return url.substring(url.lastIndexOf("/") + 1);
    }

    return (
        <div>
            {showOverlay && (
                <>
                    <div style={{ marginBottom: "20px" }}>
                        <Switch
                            onChange={() => setShowInputs(!showInputs)}
                            checked={showInputs}
                        />
                        <span style={{ marginLeft: "10px" }}>
                            {titleShowHideTimeSettings}
                        </span>
                    </div>
                    <button
                        onClick={changeColors}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
                        style={{ marginBottom: "20px" }}
                    >
                        {titleChangeStripeColors}
                    </button>
                    <div>
                        {durationsInMs
                            .map(
                                (_, index) =>
                                    index !== maxIndex && renderSlider(index)
                            )
                            .filter(Boolean)}
                        <FileBlock key={maxIndex} color={colors[maxIndex]}>
                            <div>
                                <div>
                                    {titleDuration}:{" "}
                                    {formatDuration(
                                        fileData[maxIndex].duration
                                    )}{" "}
                                    ({titleLongestFile})
                                </div>
                                <ReactAudioPlayer
                                    src={fileData[maxIndex].url}
                                    controls
                                />
                            </div>
                        </FileBlock>
                        <div
                            style={{
                                width: "100%",
                                height: "8px",
                                background: colors[maxIndex],
                                margin: "0 auto",
                            }}
                        />
                        {durationsInMs
                            .map(
                                (duration, index) =>
                                    index !== maxIndex && (
                                        <div
                                            key={index}
                                            style={{
                                                position: "relative",
                                                height: "8px",
                                                background: colors[index],
                                                marginTop: "10px",
                                                border: "1px solid #333",
                                                left: `${
                                                    (positions[index].start /
                                                        maxDuration) *
                                                    100
                                                }%`,
                                                width: `${
                                                    ((positions[index].end -
                                                        positions[index]
                                                            .start) /
                                                        maxDuration) *
                                                    100
                                                }%`,
                                            }}
                                        />
                                    )
                            )
                            .filter(Boolean)}
                    </div>
                    <div>
                        <h3>{titleOverlayResult}</h3>
                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                height: "50px",
                                background: colors[maxIndex],
                            }}
                        >
                            {durationsInMs
                                .map(
                                    (duration, index) =>
                                        index !== maxIndex && (
                                            <div
                                                key={index}
                                                style={{
                                                    position: "absolute",
                                                    height: "100%",
                                                    background: colors[index],
                                                    opacity: 0.5,
                                                    left: `${
                                                        (positions[index]
                                                            .start /
                                                            maxDuration) *
                                                        100
                                                    }%`,
                                                    width: `${
                                                        ((positions[index].end -
                                                            positions[index]
                                                                .start) /
                                                            maxDuration) *
                                                        100
                                                    }%`,
                                                }}
                                            />
                                        )
                                )
                                .filter(Boolean)}
                        </div>
                    </div>
                    <div className="px-4 py-2">
                        <ButtonContainer>
                            <StyledButton onClick={handleOverlay}>
                                {titleOverlay}
                            </StyledButton>
                        </ButtonContainer>
                    </div>
                </>
            )}
            {errorMessage && (
                <div>
                    <div className="px-4 py-2">
                        <div
                            className="w-full flex items-center bg-red-500 text-white text-sm font-bold px-4 py-3"
                            role="alert"
                        >
                            {errorMessage}
                        </div>
                    </div>
                    <div className="px-4 py-2">
                        <ButtonContainer>
                            <StyledButton onClick={reloadPage}>
                                {titleCancel}
                            </StyledButton>
                        </ButtonContainer>
                    </div>
                </div>
            )}
            {isLoading && (
                <LoaderContainer>
                    <RingLoader
                        color={"#123abc"}
                        loading={isLoading}
                        size={150}
                    />
                </LoaderContainer>
            )}
            {isProcessingFinished && !isDownloading && (
                <div>
                    <StyledMessage>{titleReady}</StyledMessage>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                        className="px-4 py-2"
                    >
                        <ReactAudioPlayer
                            src={downloadUrl}
                            controls
                            width="90%"
                            height="100px"
                        />
                    </div>
                    <ButtonContainer>
                        <a
                            href={downloadUrl}
                            download={getFileName(downloadUrl)}
                            onClick={handleDownload}
                        >
                            <StyledButton>{titleDownload}</StyledButton>
                        </a>
                        <a className="pl-2" onClick={handleDownload}>
                            <StyledButtonDelete>
                                {titleDelete}
                            </StyledButtonDelete>
                        </a>
                    </ButtonContainer>
                </div>
            )}
            {isDownloading && (
                <div>
                    <ButtonContainer>
                        <StyledButton onClick={resetDownload}>
                            {titleCancel}
                        </StyledButton>
                    </ButtonContainer>
                </div>
            )}
            <ToastContainer style={{ zIndex: 10000 }} />
        </div>
    );
};

export default Overlay;
